import {
  GitPullRequest,
  FileText,
  AlertTriangle,
  Clock,
  Shield,
  CheckCircle2,
  GitMerge,
} from 'lucide-react';
import type { IssuesPRsPillarDetails } from '../../../types/analysis.types';
import {
  MetricCard,
  SectionHeader,
  DistributionBar,
  BooleanIndicator,
  RiskImplicationsPanel,
} from './shared';
import { safeNum, formatHours, type PillarDetailsCommonProps } from './shared.utils';

interface Props extends PillarDetailsCommonProps {
  details: IssuesPRsPillarDetails;
}

export const IssuesPRsDetails: React.FC<Props> = ({
  details,
  metricBg,
  trackColor,
  theme,
  score,
}) => {
  const d = details as unknown as Record<string, unknown>;
  const openPRCount = safeNum(d.openPRCount ?? d.open_pr_count) ?? 0;
  const mergedPRCount30d = safeNum(d.mergedPRCount30d ?? d.merged_pr_count_30d) ?? 0;
  const openIssueCount = safeNum(d.openIssueCount ?? d.open_issue_count) ?? 0;
  const closedIssueCount30d = safeNum(d.closedIssueCount30d ?? d.closed_issue_count_30d) ?? 0;
  const avgPRMergeTimeHours = safeNum(d.avgPRMergeTimeHours ?? d.avg_pr_merge_time_hours);
  const avgIssueCloseTimeHours = safeNum(d.avgIssueCloseTimeHours ?? d.avg_issue_close_time_hours);
  const hasIssueTemplates = Boolean(d.hasIssueTemplates ?? d.has_issue_templates);
  const hasPRTemplates = Boolean(d.hasPRTemplates ?? d.has_pr_templates);

  const prActivity = [
    { label: 'Merged (30d)', value: mergedPRCount30d, color: '#10b981' },
    { label: 'Open', value: openPRCount, color: '#eab308' },
  ];
  const totalPRs = mergedPRCount30d + openPRCount;

  const issueActivity = [
    { label: 'Closed (30d)', value: closedIssueCount30d, color: '#10b981' },
    { label: 'Open', value: openIssueCount, color: '#f97316' },
  ];
  const totalIssues = closedIssueCount30d + openIssueCount;

  const getRiskMessage = () => {
    if (openIssueCount > 100)
      return 'High number of open issues may indicate overwhelmed maintainers or accumulating technical debt.';
    if (openPRCount > 20)
      return 'Many open PRs suggest review bottlenecks that can demotivate contributors.';
    if (avgPRMergeTimeHours && avgPRMergeTimeHours > 168)
      return 'PRs taking over a week to merge can slow development velocity.';
    return null;
  };

  const riskMessage = getRiskMessage();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Open PRs"
          value={openPRCount}
          icon={<GitPullRequest className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Merged (30d)"
          value={mergedPRCount30d}
          icon={<GitMerge className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Open Issues"
          value={openIssueCount}
          icon={<AlertTriangle className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Closed (30d)"
          value={closedIssueCount30d}
          icon={<CheckCircle2 className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
      </div>

      {(avgPRMergeTimeHours !== null || avgIssueCloseTimeHours !== null) && (
        <div className="grid grid-cols-2 gap-4">
          {avgPRMergeTimeHours !== null && (
            <MetricCard
              label="Avg PR Merge Time"
              value={formatHours(avgPRMergeTimeHours)}
              icon={<Clock className="w-4 h-4 text-secondary" />}
              metricBg={metricBg}
            />
          )}
          {avgIssueCloseTimeHours !== null && (
            <MetricCard
              label="Avg Issue Close Time"
              value={formatHours(avgIssueCloseTimeHours)}
              icon={<Clock className="w-4 h-4 text-secondary" />}
              metricBg={metricBg}
            />
          )}
        </div>
      )}

      {totalPRs > 0 && (
        <div>
          <SectionHeader
            icon={<GitPullRequest className="w-4 h-4" />}
            title="PR Activity Distribution"
          />
          <div className="space-y-2">
            {prActivity.map((item) => (
              <DistributionBar
                key={item.label}
                label={item.label}
                value={item.value}
                total={totalPRs}
                trackColor={trackColor}
                barColor={item.color}
              />
            ))}
          </div>
        </div>
      )}

      {totalIssues > 0 && (
        <div>
          <SectionHeader
            icon={<AlertTriangle className="w-4 h-4" />}
            title="Issue Activity Distribution"
          />
          <div className="space-y-2">
            {issueActivity.map((item) => (
              <DistributionBar
                key={item.label}
                label={item.label}
                value={item.value}
                total={totalIssues}
                trackColor={trackColor}
                barColor={item.color}
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <SectionHeader icon={<FileText className="w-4 h-4" />} title="Templates & Automation" />
        <div className="flex flex-wrap gap-4 text-sm">
          <BooleanIndicator
            value={hasIssueTemplates}
            trueLabel="Issue Templates"
            falseLabel="No Issue Templates"
          />
          <BooleanIndicator
            value={hasPRTemplates}
            trueLabel="PR Templates"
            falseLabel="No PR Templates"
          />
        </div>
      </div>

      {riskMessage && score < 70 && (
        <div>
          <SectionHeader icon={<Shield className="w-4 h-4" />} title="Risk Implications" />
          <RiskImplicationsPanel message={riskMessage} theme={theme} />
        </div>
      )}
    </div>
  );
};
